import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getUserStats, getUserActivity, updateProfile } from '@/lib/api';
import type { UserStats, UserActivity, Profile } from '@/types/database.types';
import { supabase } from '@/lib/supabase';
import ProfileHeader from '@/components/profile/ProfileHeader';
import StatsGrid from '@/components/profile/StatsGrid';
import ActivityList from '@/components/profile/ActivityList';
import EditProfileModal from '@/components/profile/EditProfileModal';
import FollowersModal from '@/components/profile/FollowersModal';
import FollowingModal from '@/components/profile/FollowingModal';
import DeleteAccountModal from '@/components/profile/DeleteAccountModal';
import Button from '@/components/form/Button';
import { TrashIcon } from 'lucide-react';
import UserCreditsWidget from '@/components/profile/UserCreditsWidget';
import AgeVerificationBanner from '@/components/profile/AgeVerificationBanner';

export default function Profile() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<UserStats | null>(null);
  const [activities, setActivities] = useState<UserActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [showFollowingModal, setShowFollowingModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newAvatarUrl, setNewAvatarUrl] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string>();
  const [isAgeVerified, setIsAgeVerified] = useState(false);
  const ITEMS_PER_PAGE = 10;

  const fetchProfileData = async () => {
    try {
      console.log("Profile: Fetching profile data");
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      console.log("Profile: User authenticated:", user.id);

      // Get profile data from user_profiles view to get accurate counts
      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      console.log("Profile: Profile data fetched:", profileData);

      // Get other data in parallel
      const [userStats, activityResponse] = await Promise.all([
        getUserStats(),
        getUserActivity(currentPage, ITEMS_PER_PAGE)
      ]);

      setStats(userStats);
      setActivities(activityResponse.data);
      setTotalPages(Math.ceil(activityResponse.total / ITEMS_PER_PAGE));
      
      if (profileData) {
        setProfile(profileData);
        setNewUsername(profileData.username);
        setNewAvatarUrl(profileData.avatar_url || '');
        setIsAgeVerified(profileData.age_verified || false);
      }

      // Check if age is verified in user metadata
      if (user?.user_metadata?.age_verified) {
        setIsAgeVerified(true);
      }
    } catch (error) {
      console.error('Error fetching profile data:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch profile data on mount and when page changes
  useEffect(() => {
    fetchProfileData();
  }, [currentPage]);

  const handleUpdateProfile = async () => {
    if (!newUsername.trim()) {
      setError('Username is required');
      return;
    }

    if (newUsername.length < 3 || newUsername.length > 30) {
      setError('Username must be between 3 and 30 characters');
      return;
    }

    if (!/^[a-zA-Z0-9_-]+$/.test(newUsername)) {
      setError('Username can only contain letters, numbers, underscores, and hyphens');
      return;
    }

    try {
      console.log("Profile: Updating profile with username:", newUsername, "and avatarUrl:", newAvatarUrl);
      setIsUpdating(true);
      setError(undefined);
      await updateProfile(newUsername, newAvatarUrl || null);
      toast.success('Profile updated successfully');
      setShowEditModal(false);
      
      // Refresh profile data to get updated counts
      await fetchProfileData();
    } catch (error) {
      if (error instanceof Error && error.message.includes('username_key')) {
        setError('Username is already taken');
      } else {
        toast.error('Failed to update profile');
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleFollowersCountChange = (count: number) => {
    if (profile) {
      setProfile({ ...profile, followers_count: count });
    }
  };

  const handleFollowingCountChange = (count: number) => {
    if (profile) {
      setProfile({ ...profile, following_count: count });
    }
  };

  const handleProfileImageUpdate = (newImageUrl: string) => {
    console.log("Profile: Image updated callback with URL:", newImageUrl);
    setNewAvatarUrl(newImageUrl);
    if (profile) {
      setProfile({ ...profile, avatar_url: newImageUrl });
    }
    
    // Update the profile in the database immediately
    updateProfile(profile?.username || null, newImageUrl)
      .then(() => {
        console.log("Profile: Database updated with new avatar URL");
        // Don't show toast here, it will be shown by the ImageUploadManager
      })
      .catch(error => {
        console.error("Profile: Error updating avatar in database:", error);
        toast.error('Failed to update profile image in database');
      });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Profile not found</h2>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Button
        variant="ghost"
        size="sm"
        icon={<ArrowLeftIcon className="h-4 w-4" />}
        onClick={() => navigate('/dashboard')}
        className="mb-6"
      >
        Back to Dashboard
      </Button>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
        <ProfileHeader
          profile={profile}
          onEdit={() => setShowEditModal(true)}
          onShowFollowers={() => setShowFollowersModal(true)}
          onShowFollowing={() => setShowFollowingModal(true)}
          onImageUpdate={handleProfileImageUpdate}
        />
        {stats && <StatsGrid stats={stats} />}
      </div>

      <AgeVerificationBanner isVerified={isAgeVerified} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
        <div className="md:col-span-2">
          <ActivityList
            activities={activities}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
        
        <div className="space-y-6">
          <UserCreditsWidget />
          
          <div className="bg-white rounded-lg shadow-sm p-6 border border-red-200">
            <h3 className="text-lg font-semibold text-red-800 mb-4">Danger Zone</h3>
            <p className="text-sm text-gray-600 mb-4">
              Deleting your account will permanently remove all your data, including SNAPs, votes, and comments.
              This action cannot be undone.
            </p>
            <Button
              variant="primary"
              onClick={() => setShowDeleteModal(true)}
              icon={<TrashIcon className="h-4 w-4" />}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-500"
            >
              Delete Account
            </Button>
          </div>
        </div>
      </div>

      <EditProfileModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setError(undefined);
        }}
        username={newUsername}
        avatarUrl={newAvatarUrl}
        onUsernameChange={setNewUsername}
        onAvatarUrlChange={setNewAvatarUrl}
        onSubmit={handleUpdateProfile}
        isUpdating={isUpdating}
        error={error}
        userId={profile.id}
      />

      {profile && (
        <>
          <FollowersModal
            isOpen={showFollowersModal}
            onClose={() => setShowFollowersModal(false)}
            userId={profile.id}
            onCountChange={handleFollowersCountChange}
          />

          <FollowingModal
            isOpen={showFollowingModal}
            onClose={() => setShowFollowingModal(false)}
            userId={profile.id}
            onCountChange={handleFollowingCountChange}
          />

          <DeleteAccountModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
          />
        </>
      )}
    </div>
  );
}